import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import {
  User, Mail, Phone, MapPin, Calendar, ArrowLeft, Clock, ShoppingCart,
  Award, Gift, Bell, Star, Tag, Edit, Eye, TrendingUp,
  Package, Truck, CheckCircle, XCircle, AlertCircle, Euro, Save, X, Globe, FileText
} from 'lucide-react';
import ClientPromotions from '../components/ClientPromotions';
import ClientComportementSite from '../components/ClientComportementSite';
import Modal from '../components/Modal';
import { buildReductionDisplayLines } from '../utils/commandePricingDisplay';
import {
  isBoutiqueLoyaltyLayout,
  toBoutiqueRewardCards,
  toHistoryRowView,
  BOUTIQUE_EMPTY_REWARDS,
} from '../utils/clientFideliteBoutiquePresentation';
import { formatPhoneDisplay } from '../utils/formatPhoneDisplay';
import { canManageB2bClients } from '../utils/b2bClientAccess';
import { resolveMerchantOperationalScope } from '../utils/assistanceSession';
import { canSendClientRelationEmail } from '../utils/clientRelationEmailAccess';
import ProBadge from '../components/b2b/ProBadge';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';

const ClientProfileComplete = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [orderModal, setOrderModal] = useState({
    open: false,
    loading: false,
    commande: null,
    error: '',
    commandeId: null
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    dateNaissance: '',
    genre: '',
    rue: '',
    codePostal: '',
    ville: '',
    newsletter: false,
    notifications: false,
  });
  const [pointsAdjustForm, setPointsAdjustForm] = useState({ points: '', raison: '' });
  const [pointsAdjustLoading, setPointsAdjustLoading] = useState(false);
  const [pointsAdjustMessage, setPointsAdjustMessage] = useState('');
  const [pointsAdjustError, setPointsAdjustError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isPro, setIsPro] = useState(false);
  const [moduleB2bEnabled, setModuleB2bEnabled] = useState(false);
  const [proBusy, setProBusy] = useState(false);
  const [proProfile, setProProfile] = useState(null);
  const [proProfileForm, setProProfileForm] = useState({
    raisonSociale: '',
    siren: '',
    siret: '',
    tvaIntracom: '',
    emailFacturation: '',
    facturationRue: '',
    facturationCodePostal: '',
    facturationVille: '',
    facturationPays: 'France',
  });
  const [proProfileSaving, setProProfileSaving] = useState(false);
  const [proProfileError, setProProfileError] = useState('');
  const [proDetailsOpen, setProDetailsOpen] = useState(false);
  const { toasts, toast, removeToast } = useToast();

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') {
      return '0.00';
    }
    const numberValue = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (Number.isNaN(numberValue)) {
      return '0.00';
    }
    return numberValue.toFixed(2);
  };

  const formatDateFr = (value, withTime = false) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return withTime
      ? d.toLocaleString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const firstOrderDateValue = (c, data) => {
    if (c?.premiereCommandeDate) return c.premiereCommandeDate;
    const list = data?.commandes || [];
    let min = null;
    list.forEach((cmd) => {
      const raw = cmd?.dateCommande || cmd?.date;
      if (!raw) return;
      const t = new Date(raw).getTime();
      if (!Number.isFinite(t)) return;
      if (min == null || t < min) min = t;
    });
    return min == null ? null : new Date(min).toISOString();
  };

  const firstOrderLabel = (c, data) => {
    const formatted = formatDateFr(firstOrderDateValue(c, data));
    if (formatted) return formatted;
    const n = data?.statistiques?.totalCommandes || data?.commandes?.length || 0;
    if (n > 0) return 'Date non disponible';
    return 'Aucune commande';
  };

  const lastOrderLabel = (c, data) => {
    if (c?.derniereCommandeDate) return formatDateFr(c.derniereCommandeDate) || 'Date non disponible';
    const list = data?.commandes || [];
    let max = null;
    list.forEach((cmd) => {
      const raw = cmd?.dateCommande || cmd?.date;
      if (!raw) return;
      const t = new Date(raw).getTime();
      if (!Number.isFinite(t)) return;
      if (max == null || t > max) max = t;
    });
    if (max != null) return formatDateFr(new Date(max).toISOString());
    const n = data?.statistiques?.totalCommandes || list.length || 0;
    if (n > 0) return 'Date non disponible';
    return 'Aucune commande';
  };

  const registrationLabel = (c) => {
    if (c?.dateInscriptionStatus === 'KNOWN' && c.dateInscription) {
      return formatDateFr(c.dateInscription);
    }
    return 'Historique antérieur non disponible';
  };

  const presentSinceHint = (c) => {
    if (c?.dateInscriptionStatus === 'KNOWN') return null;
    if (!c?.presentSince) return null;
    const d = formatDateFr(c.presentSince);
    if (!d) return null;
    return `Compte présent dans Clickorico depuis au moins ${d}`;
  };

  const lastLoginLabel = (c) => {
    if (c?.derniereConnexionStatus === 'AUTHENTICATED' && c?.derniereConnexion) {
      return formatDateFr(c.derniereConnexion, true);
    }
    return 'Historique antérieur non disponible';
  };

  const lastActivityLabel = (c) => {
    if (c?.derniereActiviteSite) return formatDateFr(c.derniereActiviteSite, true);
    return 'Aucune activité identifiée';
  };

  const newsletterSourceLabel = (source) => {
    if (source === 'REGISTER') return 'Inscription';
    if (source === 'PROFILE') return 'Profil';
    return source || null;
  };

  const consentLabel = (status) => {
    if (status === 'EXPLICIT_OPT_IN') return 'Consentement explicite';
    if (status === 'EXPLICIT_OPT_OUT') return 'Désactivé';
    if (status === 'LEGACY_DEFAULT') return 'Statut hérité — consentement non vérifiable';
    return 'Non renseigné';
  };

  const newsletterView = (c) => {
    if (c?.newsletter === true && c?.newsletterConsentAt) {
      return { kind: 'optin' };
    }
    if (c?.newsletter === false || c?.newsletterStatus === 'EXPLICIT_OPT_OUT') {
      return { kind: 'optout' };
    }
    if (c?.newsletter === true || c?.newsletterStatus === 'LEGACY_DEFAULT') {
      return { kind: 'legacy' };
    }
    return { kind: 'unknown' };
  };

  const handleViewOrderDetail = async (commandeId) => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[ClientProfile] Voir détail commande', commandeId);
    }

    setOrderModal({
      open: true,
      loading: true,
      commande: null,
      error: '',
      commandeId
    });

    try {
      const detail = await apiService.getCommande(commandeId);
      const resolvedCommande = detail?.commande || detail || null;

      setOrderModal((prev) => ({
        ...prev,
        loading: false,
        commande: resolvedCommande,
        error: resolvedCommande ? '' : 'Commande introuvable'
      }));
    } catch (err) {
      console.error('[ClientProfile] Erreur chargement commande', err);
      setOrderModal((prev) => ({
        ...prev,
        loading: false,
        commande: null,
        error: err?.message || 'Impossible de charger la commande'
      }));
    }
  };

  const closeOrderModal = () => {
    setOrderModal({
      open: false,
      loading: false,
      commande: null,
      error: '',
      commandeId: null
    });
  };

  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    return null;
  };

  useEffect(() => {
    // Récupérer l'utilisateur actuel pour vérifier les permissions
    try {
      const userData = localStorage.getItem('admin_user');
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      }
    } catch (error) {
      console.error('Erreur parsing user data:', error);
    }
    const tab = searchParams.get('tab');
    if (tab === 'comportement') {
      setActiveTab('comportement');
    }
    fetchClientAnalytics();
  }, [clientId]);

  useEffect(() => {
    const shopId =
      searchParams.get('commercantId') ||
      resolveMerchantOperationalScope(getCurrentUser()).commercantId ||
      (getCurrentUser()?.role === 'COMMERCANT' ? getCurrentUser()?.commercantId : null) ||
      null;
    if (!isPro || !shopId || !clientId) {
      setProProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getClientProfessionalProfile(shopId, clientId);
        if (cancelled) return;
        setProProfile(data);
        const p = data?.professionalProfile || {};
        setProProfileForm({
          raisonSociale: p.raisonSociale || '',
          siren: p.siren || '',
          siret: p.siret || '',
          tvaIntracom: p.tvaIntracom || '',
          emailFacturation: p.emailFacturation || '',
          facturationRue: p.facturationRue || '',
          facturationCodePostal: p.facturationCodePostal || '',
          facturationVille: p.facturationVille || '',
          facturationPays: p.facturationPays || 'France',
        });
        setProProfileError('');
      } catch {
        if (!cancelled) setProProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPro, clientId, searchParams]);

  const fetchClientAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      // Récupérer le commercantId depuis l'URL si présent (pour SUPER_ADMIN qui consulte depuis profil commerçant)
      const commercantIdFromUrl = searchParams.get('commercantId');
      // Pour un COMMERCANT, le backend filtre automatiquement par req.user.commercantId
      // Pour un SUPER_ADMIN, on passe le commercantId de l'URL si présent
      const commercantId = commercantIdFromUrl || null;
      
      console.log('🔍 Fetching analytics for client:', clientId, 'commercantId:', commercantId);
      const data = await apiService.getClientAnalytics(clientId, commercantId);
      console.log('✅ Analytics data received:', data);
      setAnalytics(data);
      if (data.client && typeof data.client.isPro === 'boolean') {
        setIsPro(Boolean(data.client.isPro));
      }
      const shopId = commercantId
        || resolveMerchantOperationalScope(getCurrentUser()).commercantId
        || null;
      if (shopId) {
        try {
          const cData = await apiService.getCommercant(shopId);
          const c = cData?.commercant || cData;
          setModuleB2bEnabled(Boolean(c?.moduleB2b));
        } catch {
          setModuleB2bEnabled(false);
        }
      }
      
      // Initialiser le formulaire d'édition avec les données du client
      if (data.client) {
        setEditForm({
          nom: data.client.nom || '',
          prenom: data.client.prenom || '',
          email: data.client.email || '',
          telephone: data.client.telephone || '',
          dateNaissance: data.client.dateNaissance ? new Date(data.client.dateNaissance).toISOString().split('T')[0] : '',
          genre: data.client.genre || '',
          rue: data.client.rue || '',
          codePostal: data.client.codePostal || '',
          ville: data.client.ville || '',
          newsletter: Boolean(data.client.newsletter),
          notifications: Boolean(data.client.notifications),
        });
      }
    } catch (err) {
      console.error('❌ Error fetching analytics:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        stack: err.stack
      });
      
      // Message d'erreur plus détaillé
      let errorMessage = 'Erreur lors du chargement des données client';
      if (err.response?.status === 404) {
        errorMessage = 'Client non trouvé ou accès non autorisé';
      } else if (err.response?.status === 403) {
        errorMessage = 'Accès refusé : vous n\'avez pas les permissions pour voir ce client';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditError('');
    // Réinitialiser le formulaire avec les données actuelles
    if (analytics.client) {
      setEditForm({
        nom: analytics.client.nom || '',
        prenom: analytics.client.prenom || '',
        email: analytics.client.email || '',
        telephone: analytics.client.telephone || '',
        dateNaissance: analytics.client.dateNaissance ? new Date(analytics.client.dateNaissance).toISOString().split('T')[0] : '',
        genre: analytics.client.genre || '',
        rue: analytics.client.rue || '',
        codePostal: analytics.client.codePostal || '',
        ville: analytics.client.ville || '',
        newsletter: Boolean(analytics.client.newsletter),
        notifications: Boolean(analytics.client.notifications),
      });
    }
  };

  const handleSaveEdit = async () => {
    try {
      setEditLoading(true);
      setEditError('');
      
      // Préparer les données à envoyer (seulement les champs modifiables)
      const updateData = {
        nom: editForm.nom.trim() || null,
        prenom: editForm.prenom.trim() || null,
        email: editForm.email.trim(),
        telephone: editForm.telephone.trim() || null,
        dateNaissance: editForm.dateNaissance || null,
        genre: editForm.genre || null,
        rue: editForm.rue?.trim() || null,
        codePostal: editForm.codePostal?.trim() || null,
        ville: editForm.ville?.trim() || null,
        newsletter: Boolean(editForm.newsletter),
        notifications: Boolean(editForm.notifications),
      };
      
      // Appeler l'API pour mettre à jour
      await apiService.updateUser(clientId, updateData);
      
      // Recharger les données
      await fetchClientAnalytics();
      
      setIsEditing(false);
    } catch (err) {
      console.error('❌ Error updating client:', err);
      setEditError(err.response?.data?.error || err.message || 'Erreur lors de la mise à jour du client');
    } finally {
      setEditLoading(false);
    }
  };

  const handleAdjustPoints = async (e) => {
    e.preventDefault();
    const points = parseInt(pointsAdjustForm.points, 10);
    const raison = (pointsAdjustForm.raison || '').trim();
    if (!Number.isFinite(points) || points === 0) {
      setPointsAdjustError('Indiquez un nombre de points différent de 0 (positif = crédit, négatif = débit).');
      return;
    }
    if (raison.length < 3) {
      setPointsAdjustError('Indiquez une raison (min. 3 caractères).');
      return;
    }
    try {
      setPointsAdjustLoading(true);
      setPointsAdjustError('');
      setPointsAdjustMessage('');
      const result = await apiService.ajusterPointsClient(clientId, points, raison);
      const nouveau = result?.pointsClickorico?.nouveauxPoints;
      setPointsAdjustMessage(
        Number.isFinite(nouveau)
          ? `Points mis à jour : solde Clickorico = ${nouveau} pts`
          : 'Points ajustés avec succès',
      );
      setPointsAdjustForm({ points: '', raison: '' });
      await fetchClientAnalytics();
    } catch (err) {
      setPointsAdjustError(
        err?.data?.error || err?.response?.data?.error || err?.message || 'Erreur lors de l’ajustement',
      );
    } finally {
      setPointsAdjustLoading(false);
    }
  };

  const getStatusIcon = (statut) => {
    switch (statut) {
      case 'LIVREE': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'EN_PREPARATION': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'EN_LIVRAISON': return <Truck className="h-4 w-4 text-blue-500" />;
      case 'ANNULEE': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (statut) => {
    switch (statut) {
      case 'LIVREE': return 'bg-green-100 text-green-800 border-green-200';
      case 'EN_PREPARATION': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'EN_LIVRAISON': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ANNULEE': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getLoyaltyLevelColor = (level) => {
    switch (level?.toUpperCase()) {
      case 'PLATINUM': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'GOLD': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'SILVER': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'BRONZE': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProchainNiveauLabel = (clientData) => {
    if (!clientData) return '—';
    if (clientData.niveauMaximum) return 'Niveau maximum atteint';
    if (clientData.prochainNiveauFidelite) {
      const pointsRestants = clientData.pointsPourProchainNiveau ?? 0;
      return `${pointsRestants} points pour ${clientData.prochainNiveauFidelite}`;
    }
    return '—';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-[1800px] mx-auto">
          <button
            onClick={() => navigate('/clients')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux clients
          </button>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Erreur</h1>
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchClientAnalytics}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics.client) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-[1800px] mx-auto">
          <button
            onClick={() => navigate('/clients')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux clients
          </button>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Client non trouvé</h1>
            <p className="text-gray-600">Les données de ce client ne sont pas disponibles.</p>
          </div>
        </div>
      </div>
    );
  }

  const { client } = analytics;

  const comportementCommercantId =
    searchParams.get('commercantId') ||
    resolveMerchantOperationalScope(currentUser).commercantId ||
    (currentUser?.role === 'COMMERCANT' ? currentUser.commercantId : null) ||
    null;

  const isSuperAdminViewer = ['SUPER_ADMIN', 'ADMIN'].includes(currentUser?.role);
  const canTogglePro = Boolean(comportementCommercantId) && canManageB2bClients(currentUser);

  const handleToggleIsPro = async (next) => {
    if (!comportementCommercantId || !client?.id) return;
    setProBusy(true);
    try {
      const row = await apiService.patchB2bClientPro(comportementCommercantId, client.id, next);
      setIsPro(Boolean(row?.isPro));
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Impossible de modifier le statut PRO';
      setError(msg);
    } finally {
      setProBusy(false);
    }
  };

  const handleSaveProfessionalProfile = async (e) => {
    e.preventDefault();
    if (!comportementCommercantId || !client?.id || !canTogglePro) return;
    setProProfileSaving(true);
    setProProfileError('');
    try {
      const data = await apiService.putClientProfessionalProfile(
        comportementCommercantId,
        client.id,
        proProfileForm
      );
      setProProfile(data);
      toast.success('Vos informations professionnelles ont bien été enregistrées.');
    } catch (err) {
      setProProfileError(
        err?.response?.data?.error?.message || err?.message || 'Impossible d’enregistrer le profil professionnel'
      );
    } finally {
      setProProfileSaving(false);
    }
  };

  const canEmailClient = canSendClientRelationEmail(currentUser);

  const handleSendClientEmail = async (e) => {
    e.preventDefault();
    if (!client?.id || emailSending) return;
    const subject = emailSubject.trim();
    const message = emailMessage.trim();
    if (!subject || !message) {
      setEmailError('Le sujet et le message sont obligatoires.');
      return;
    }
    setEmailSending(true);
    setEmailError('');
    try {
      await apiService.sendClientRelationEmail(client.id, { subject, message });
      setEmailModalOpen(false);
      setEmailSubject('');
      setEmailMessage('');
      toast.success('Email envoyé au client.');
    } catch (err) {
      setEmailError(err?.message || 'L’email n’a pas pu être envoyé.');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/clients')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux clients
          </button>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {client.prenom} {client.nom}
                  </h1>
                  <p className="text-gray-600 mt-1">{client.email}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${
                      client.statut === 'ACTIF' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800 border-gray-200'
                    }`}>
                      {client.statut}
                    </span>
                    {client.loyaltyContext !== 'MERCHANT' && client.niveauFidelite && (
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getLoyaltyLevelColor(client.niveauFidelite)}`}>
                      {client.niveauFidelite}
                    </span>
                    )}
                    {client.loyaltyContext === 'MERCHANT' && client.niveauFidelite && (
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getLoyaltyLevelColor(client.niveauFidelite)}`}>
                      Niveau Clickorico · {client.niveauFidelite}
                    </span>
                    )}
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${
                      isPro ? 'bg-indigo-50 text-indigo-800 border-indigo-200' : 'bg-gray-50 text-gray-700 border-gray-200'
                    }`}>
                      {isPro ? 'PRO' : 'Particulier'}
                    </span>
                    {isPro ? <ProBadge size="line" className="ml-1" /> : null}
                  </div>
                  {canTogglePro && (
                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-sm text-gray-600">Statut boutique</span>
                      <button
                        type="button"
                        disabled={proBusy || !moduleB2bEnabled}
                        onClick={() => handleToggleIsPro(!isPro)}
                        className={`px-3 py-1.5 text-sm rounded-lg border ${
                          isPro
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-700 border-gray-300'
                        } disabled:opacity-50`}
                      >
                        {isPro ? 'PRO' : 'PARTICULIER'}
                      </button>
                      {isPro ? (
                        <span className="text-xs text-gray-500">
                          Reconnu PRO — tarifs activés seulement si le profil est complet
                        </span>
                      ) : null}
                      {!moduleB2bEnabled && (
                        <span className="text-xs text-amber-700">Module B2B non activé — contactez Clickorico</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                {canEmailClient && (
                  <button
                    type="button"
                    className="mb-3 inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
                    onClick={() => {
                      setEmailError('');
                      setEmailModalOpen(true);
                    }}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Envoyer un email
                  </button>
                )}
                <p className="text-sm text-gray-500">Première commande</p>
                <p className="text-lg font-semibold text-gray-900">
                  {firstOrderLabel(client, analytics)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {isPro && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">STATUT PROFESSIONNEL</h2>
              <ProBadge size="line" />
            </div>
            <p className="text-sm text-gray-800 font-medium">
              {proProfile?.isComplete
                ? 'Client professionnel'
                : 'Client professionnel reconnu'}
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className={`rounded-lg border px-3 py-2 ${
                proProfile?.isComplete
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <p className="text-xs text-gray-500">Profil professionnel</p>
                <p className="font-semibold text-gray-900">
                  {proProfile?.isComplete ? 'Complet' : 'À compléter'}
                </p>
              </div>
              <div className={`rounded-lg border px-3 py-2 ${
                proProfile?.pricingActive
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <p className="text-xs text-gray-500">Tarifs PRO</p>
                <p className="font-semibold text-gray-900">
                  {proProfile?.pricingActive ? 'Activés' : 'Non activés'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setProDetailsOpen((open) => !open)}
              aria-expanded={proDetailsOpen}
              className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              {proDetailsOpen ? 'Masquer les informations' : 'Afficher les informations'}
            </button>
            {proDetailsOpen ? (
              <>
                {!proProfile?.isComplete && Array.isArray(proProfile?.missingFields) && proProfile.missingFields.length > 0 ? (
                  <p className="mt-3 text-sm text-amber-800">
                    Champs manquants : {proProfile.missingFields.map((f) => f.label).join(', ')}.
                  </p>
                ) : null}
                <p className="mt-2 text-xs text-gray-500">
                  Reconnu comme client professionnel. Les tarifs PRO ne s’activent que lorsque le profil
                  est complet.
                </p>
                <h3 className="text-md font-bold text-gray-900 mt-6 mb-4">INFORMATIONS PROFESSIONNELLES</h3>
                <form onSubmit={handleSaveProfessionalProfile} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ['raisonSociale', 'Raison sociale *'],
                    ['siren', 'SIREN *'],
                    ['siret', 'SIRET *'],
                    ['tvaIntracom', 'TVA intra — si applicable'],
                    ['emailFacturation', 'Email facturation *'],
                    ['facturationRue', 'Adresse *'],
                    ['facturationCodePostal', 'Code postal *'],
                    ['facturationVille', 'Ville *'],
                    ['facturationPays', 'Pays *'],
                  ].map(([key, label]) => (
                    <div key={key} className={key === 'facturationRue' || key === 'raisonSociale' ? 'md:col-span-2' : ''}>
                      <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
                      <input
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50"
                        value={proProfileForm[key] || ''}
                        disabled={!canTogglePro}
                        onChange={(e) =>
                          setProProfileForm((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    </div>
                  ))}
                  {proProfileError ? (
                    <p className="md:col-span-2 text-sm text-red-700">{proProfileError}</p>
                  ) : null}
                  {canTogglePro ? (
                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        disabled={proProfileSaving}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold disabled:opacity-50"
                      >
                        {proProfileSaving ? 'Enregistrement…' : 'Enregistrer le profil professionnel'}
                      </button>
                    </div>
                  ) : (
                    <p className="md:col-span-2 text-xs text-gray-500">Lecture seule pour votre rôle.</p>
                  )}
                </form>
              </>
            ) : null}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: Eye },
              { id: 'comportement', label: 'Comportement site', icon: Globe },
              { id: 'orders', label: 'Commandes', icon: ShoppingCart },
              { id: 'loyalty', label: 'Fidélité', icon: Award },
              { id: 'promotions', label: 'Promotions attribuées', icon: Tag },
              { id: 'addresses', label: 'Adresses', icon: MapPin },
              { id: 'reviews', label: 'Avis', icon: Star }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Statistiques principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShoppingCart className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total commandes</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {analytics.statistiques?.totalCommandes || 0}
                    </p>
                    {analytics.statistiques?.commandesPayees != null && (
                      <p className="text-xs text-gray-500 mt-1">
                        dont {analytics.statistiques.commandesPayees} payée{analytics.statistiques.commandesPayees > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Euro className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total dépensé</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatAmount(analytics.statistiques?.totalDepense)}€
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Panier moyen</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatAmount(analytics.statistiques?.panierMoyen)}€
                    </p>
                    <p className="text-xs text-gray-500 mt-1">CA net / commandes payées</p>
                    {analytics.statistiques?.commandesPayees > 0 && (
                      <p className="text-xs text-gray-500">
                        sur {analytics.statistiques.commandesPayees} commande{analytics.statistiques.commandesPayees > 1 ? 's' : ''} payée{analytics.statistiques.commandesPayees > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Award className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">
                      {client.loyaltyContext === 'MERCHANT' ? 'Points fidélité boutique' : 'Points Clickorico'}
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {client.pointsFidelite ?? 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Globe className="h-6 w-6 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Comportement sur le site</p>
                  <p className="text-sm text-gray-600">
                    Activité identifiée : passages boutique, produits consultés, panier et parcours
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('comportement')}
                className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700"
              >
                Ouvrir la fiche site
              </button>
            </div>

            {/* Informations détaillées */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <User className="h-5 w-5 mr-2" />
                    Informations personnelles
                  </h3>
                  {currentUser?.role === 'SUPER_ADMIN' && !isEditing && (
                    <button
                      onClick={handleEdit}
                      className="flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-md transition-colors"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Modifier
                    </button>
                  )}
                </div>
                
                {isEditing && currentUser?.role === 'SUPER_ADMIN' ? (
                  <div className="space-y-4">
                    {editError && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                        {editError}
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                        <input
                          type="text"
                          value={editForm.prenom}
                          onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Prénom"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                        <input
                          type="text"
                          value={editForm.nom}
                          onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Nom"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                      <input
                        type="tel"
                        value={editForm.telephone}
                        onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                      <input
                        type="date"
                        value={editForm.dateNaissance}
                        onChange={(e) => setEditForm({ ...editForm, dateNaissance: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Genre</label>
                      <select
                        value={editForm.genre}
                        onChange={(e) => setEditForm({ ...editForm, genre: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Non renseigné</option>
                        <option value="HOMME">Homme</option>
                        <option value="FEMME">Femme</option>
                      <option value="AUTRE">Autre</option>
                    </select>
                    </div>

                    {client.addressSource !== 'ADDRESS_DEFAULT' && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Adresse (fiche profil)</label>
                        <input
                          type="text"
                          value={editForm.rue}
                          onChange={(e) => setEditForm({ ...editForm, rue: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Rue"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={editForm.codePostal}
                          onChange={(e) => setEditForm({ ...editForm, codePostal: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Code postal"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <input
                          type="text"
                          value={editForm.ville}
                          onChange={(e) => setEditForm({ ...editForm, ville: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Ville"
                        />
                      </div>
                    </div>
                    )}
                    {client.addressSource === 'ADDRESS_DEFAULT' && (
                      <p className="text-xs text-gray-500">
                        L’adresse affichée est l’adresse principale. Pour la modifier, utilisez l’onglet Adresses.
                        <button
                          type="button"
                          onClick={() => { setIsEditing(false); setActiveTab('addresses'); }}
                          className="ml-1 text-indigo-600 hover:underline"
                        >
                          Ouvrir Adresses
                        </button>
                      </p>
                    )}

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Newsletter</p>
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={Boolean(editForm.newsletter)}
                          onChange={(e) => setEditForm({ ...editForm, newsletter: e.target.checked })}
                          className="mr-2"
                        />
                        Newsletter active (cocher/décocher enregistre un choix explicite)
                      </label>
                      <p className="text-xs text-gray-500">{consentLabel(client.newsletterStatus)}</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Préférences de notifications</p>
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={Boolean(editForm.notifications)}
                          onChange={(e) => setEditForm({ ...editForm, notifications: e.target.checked })}
                          className="mr-2"
                        />
                        Notifications actives
                      </label>
                      <p className="text-xs text-gray-500">
                        {editForm.notifications ? 'Activées' : 'Désactivées'}
                      </p>
                    </div>
                   
                    <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                      <button
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        <X className="h-4 w-4 inline mr-1" />
                        Annuler
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={editLoading || !editForm.email.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
                      >
                        {editLoading ? (
                          <>Chargement...</>
                        ) : (
                          <>
                            <Save className="h-4 w-4 inline mr-1" />
                            Enregistrer
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{client.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Téléphone</p>
                      <p className="font-medium">{client.telephone ? formatPhoneDisplay(client.telephone) : 'Non renseigné'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Date d'inscription</p>
                      <p className="font-medium">{registrationLabel(client)}</p>
                      {presentSinceHint(client) && (
                        <p className="text-xs text-gray-500">{presentSinceHint(client)}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Première commande</p>
                      <p className="font-medium">{firstOrderLabel(client, analytics)}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Dernière commande</p>
                      <p className="font-medium">{lastOrderLabel(client, analytics)}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Dernière connexion</p>
                      <p className="font-medium">{lastLoginLabel(client)}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Globe className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Dernière activité sur le site</p>
                      <p className="font-medium">{lastActivityLabel(client)}</p>
                      <p className="text-xs text-gray-500">Activité associée à ce compte (identifiée, distincte de la connexion)</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Date de naissance</p>
                      <p className="font-medium">
                        {client.dateNaissance
                          ? new Date(client.dateNaissance).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : 'Non renseigné'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <User className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Genre</p>
                      <p className="font-medium capitalize">{client.genre || 'Non renseigné'}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">
                        {client.addressSource === 'USER_LEGACY'
                          ? 'Adresse (fiche profil)'
                          : 'Adresse'}
                      </p>
                      <p className="font-medium">
                        {[client.rue, client.codePostal, client.ville].filter(Boolean).join(', ') || 'Non renseigné'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <FileText className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Consentements & conditions</p>
                      <div className="mt-3 space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">CGU Clickorico</p>
                          {client.cguAcceptedAt ? (
                            <>
                              <p className="font-medium text-emerald-700">✅ Acceptées</p>
                              <p className="text-xs text-gray-500">
                                Date : {formatDateFr(client.cguAcceptedAt, true) || 'Date non disponible'}
                              </p>
                              <p className="text-xs text-gray-500">
                                Révision :{' '}
                                {formatDateFr(client.cguRevisionPublishedAt) ||
                                  client.cguRevisionTitle ||
                                  (client.cguRevisionId ? String(client.cguRevisionId).slice(-8) : 'identifiée')}
                              </p>
                            </>
                          ) : (
                            <p className="font-medium text-gray-700">
                              Historique antérieur — acceptation non enregistrée
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Newsletter</p>
                          {newsletterView(client).kind === 'optin' ? (
                            <>
                              <p className="font-medium text-emerald-700">✅ Abonné</p>
                              <p className="text-xs text-gray-500">Consentement explicite</p>
                              {client.newsletterConsentAt ? (
                                <p className="text-xs text-gray-500">
                                  Date : {formatDateFr(client.newsletterConsentAt, true)}
                                </p>
                              ) : null}
                              {newsletterSourceLabel(client.newsletterConsentSource) ? (
                                <p className="text-xs text-gray-500">
                                  Source : {newsletterSourceLabel(client.newsletterConsentSource)}
                                </p>
                              ) : null}
                            </>
                          ) : newsletterView(client).kind === 'optout' ? (
                            <p className="font-medium text-gray-800">❌ Non abonné</p>
                          ) : newsletterView(client).kind === 'legacy' ? (
                            <p className="font-medium text-amber-800">
                              ⚠ Statut hérité — consentement non vérifiable
                            </p>
                          ) : (
                            <p className="font-medium">{consentLabel(client.newsletterStatus)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Bell className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Préférences de notifications</p>
                      <p className="font-medium">{client.notifications ? 'Activées' : 'Désactivées'}</p>
                    </div>
                  </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Award className="h-5 w-5 mr-2" />
                  Programme de fidélité
                </h3>
                <div className="space-y-4">
                  {client.niveauFidelite && (
                  <div>
                    <p className="text-sm text-gray-500">Niveau Clickorico</p>
                    <div className="flex items-center mt-1">
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getLoyaltyLevelColor(client.niveauFidelite)}`}>
                        {client.niveauFidelite}
                      </span>
                    </div>
                  </div>
                  )}
                  {!client.niveauFidelite && (
                  <div>
                    <p className="text-sm text-gray-500">Niveau Clickorico</p>
                    <p className="font-medium">Non renseigné</p>
                  </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-gray-500">
                      {client.loyaltyContext === 'MERCHANT' ? 'Points boutique' : 'Points Clickorico'}
                    </p>
                    <p className="text-2xl font-bold text-orange-600">
                      {client.pointsFidelite ?? 0} points
                    </p>
                  </div>

                  {client.loyaltyContext === 'MERCHANT' && (
                  <div>
                    <p className="text-sm text-gray-500">Points Clickorico</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {client.pointsClickorico ?? 0} points
                    </p>
                  </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-gray-500">Carte Clickorico</p>
                    <p className="text-lg font-semibold text-gray-900 font-mono">
                      {client.numeroCarte || 'Non renseigné'}
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      Points nécessaires pour le niveau Clickorico suivant :
                    </p>
                    <p className="font-medium text-gray-900">
                      {getProchainNiveauLabel(client)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Tag className="h-5 w-5 mr-2" />
                Segments calculés
              </h3>
              {analytics.tags && analytics.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {analytics.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex px-3 py-1 rounded-full text-sm font-medium bg-indigo-50 text-indigo-800 border border-indigo-100"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Aucun segment calculé pour l’instant</p>
              )}
              <p className="text-xs text-gray-500 mt-3">
                VIP : CA net &gt; 1000 € · Gros panier : panier moyen &gt; 50 € · Fidèle : &gt; 10 commandes payées · Varié : &gt; 3 commerces payés · Parrain : &gt; 500 points Clickorico
              </p>
            </div>
          </div>
        )}

        {activeTab === 'comportement' && (
          <div className="bg-white rounded-lg shadow p-6">
            <ClientComportementSite
              clientId={clientId}
              commercantId={comportementCommercantId}
              allowCommercantPicker={isSuperAdminViewer && !comportementCommercantId}
            />
          </div>
        )}

        {/* Historique des commandes */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ShoppingCart className="h-5 w-5 mr-2" />
                Historique des commandes
              </h3>
            </div>
            
            {analytics.commandes && analytics.commandes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commande
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Heure
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commerçant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Articles
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.commandes.map((commande) => (
                      <tr
                        key={commande.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleViewOrderDetail(commande.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewOrderDetail(commande.id);
                          }
                        }}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {commande.numero || `#${commande.id.slice(-8)}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {commande.dateCommande ? 
                            new Date(commande.dateCommande).toLocaleString('fr-FR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) :
                            (commande.date ? new Date(commande.date).toLocaleDateString('fr-FR') : 'N/A')
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {typeof commande.commercant === 'string' ? commande.commercant : (commande.commercant?.nom || 'Inconnu')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatAmount(commande.total)} €
                          {commande.fraisLivraison > 0 && (
                            <span className="block text-xs text-gray-500 mt-1">
                              + {formatAmount(commande.fraisLivraison)} € livraison
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {commande.nombreArticles} article{commande.nombreArticles > 1 ? 's' : ''}
                          {commande.modeRetrait && (
                            <span className="block text-xs text-gray-500 mt-1">
                              {commande.modeRetrait === 'LIVRAISON' ? '🚚 Livraison' : '🏪 Retrait'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(commande.statut)}
                            <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(commande.statut)}`}>
                              {commande.statut}
                            </span>
                          </div>
                        </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <span className="text-indigo-600">
                          Voir le détail
                        </span>
                      </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune commande</h3>
                <p className="text-gray-500">Ce client n'a pas encore passé de commande.</p>
              </div>
            )}
          </div>
        )}

        {/* Adresses */}
        {activeTab === 'addresses' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <MapPin className="h-5 w-5 mr-2" />
                Adresses enregistrées
              </h3>
            </div>
            
            <div className="p-6">
              {analytics.addresses && analytics.addresses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analytics.addresses.map((address) => (
                    <div key={address.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {address.label || address.type || 'Adresse enregistrée'}
                        </span>
                        {address.isDefault && (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Principale
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {address.rue}<br />
                        {address.codePostal} {address.ville}<br />
                        {address.pays}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune adresse</h3>
                  <p className="text-gray-500">Ce client n'a pas encore enregistré d'adresse.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Avis */}
        {activeTab === 'reviews' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Star className="h-5 w-5 mr-2 text-yellow-500" />
                Avis du client
              </h3>
            </div>
            
            <div className="p-6">
              {analytics.reviews && analytics.reviews.length > 0 ? (
                <div className="space-y-4">
                  {analytics.reviews.map((review) => (
                    <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {review.commercant?.nom || 'Avis plateforme'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(review.createdAt).toLocaleDateString('fr-FR')}
                            {review.commande && (
                              <span className="ml-2 text-gray-400">
                                · Commande{' '}
                                {review.commande.numero ||
                                  review.commande.id?.slice(0, 8)}
                              </span>
                            )}
                            {review.rendezVous && !review.commande && (
                              <span className="ml-2 text-gray-400">
                                · RDV{' '}
                                {review.rendezVous.label ||
                                  new Date(review.rendezVous.dateDebut).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center space-x-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-4 w-4 ${
                                star <= (review.note || 0)
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      {review.commentaire && (
                        <p className="mt-3 text-gray-700">
                          {review.commentaire}
                        </p>
                      )}
                      <p className="mt-3 text-xs font-semibold uppercase text-gray-400">
                        {review.type === 'PLATFORM' ? 'Avis plateforme' : 'Avis commerçant'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun avis</h3>
                  <p className="text-gray-500">Ce client n'a pas encore laissé d'avis.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Promotions personnalisées */}
        {activeTab === 'promotions' && (
          <div className="bg-white rounded-lg shadow p-6">
            <ClientPromotions
              clientId={clientId}
              clientName={`${client.prenom} ${client.nom}`}
              commercantId={comportementCommercantId}
            />
          </div>
        )}

        {/* Fidélité détaillée */}
        {activeTab === 'loyalty' && (
          <div className="space-y-6">
            {isBoutiqueLoyaltyLayout(analytics) ? (
              <>
                {/* Layout boutique F2/F2.5 — commerce ciblé uniquement */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Award className="h-5 w-5 mr-2" />
                    Fidélité chez{' '}
                    {(
                      analytics.fideliteBoutique?.commercantNom ||
                      'cette boutique'
                    ).toUpperCase()}
                  </h3>
                  <p className="text-3xl font-bold text-gray-900 mb-6">
                    {(
                      analytics.fideliteBoutique?.points ??
                      analytics.catalogueBoutique?.solde ??
                      0
                    ).toLocaleString('fr-FR')}{' '}
                    <span className="text-lg font-medium text-gray-600">points disponibles</span>
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg bg-gray-50 p-4">
                      <p className="text-sm text-gray-500">Commandes</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {analytics.fideliteBoutique?.totalCommandes ??
                          analytics.statistiques?.totalCommandes ??
                          0}{' '}
                        commandes
                      </p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-4">
                      <p className="text-sm text-gray-500">Points utilisés</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {(analytics.fideliteBoutique?.pointsUtilises ?? 0).toLocaleString('fr-FR')}{' '}
                        points utilisés
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Gift className="h-5 w-5 mr-2" />
                    Récompenses
                  </h3>
                  {(!analytics.catalogueBoutique?.programmeActif ||
                    toBoutiqueRewardCards(analytics.catalogueBoutique).length === 0) && (
                    <p className="text-sm text-gray-500">{BOUTIQUE_EMPTY_REWARDS}</p>
                  )}
                  {analytics.catalogueBoutique?.programmeActif &&
                    toBoutiqueRewardCards(analytics.catalogueBoutique).length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {toBoutiqueRewardCards(analytics.catalogueBoutique).map((card) => (
                          <div
                            key={card.key}
                            className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                          >
                            <p className="font-semibold text-gray-900">{card.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{card.coutLabel}</p>
                            <p
                              className={`text-sm font-medium mt-3 ${
                                card.accessible ? 'text-green-700' : 'text-amber-700'
                              }`}
                            >
                              {card.accessible ? '✓ ' : ''}
                              {card.statusLabel}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                </div>

                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Gift className="h-5 w-5 mr-2" />
                      Historique de fidélité
                    </h3>
                  </div>
                  <div className="p-6">
                    {analytics.transactionsFidelite && analytics.transactionsFidelite.length > 0 ? (
                      <div className="space-y-3">
                        {analytics.transactionsFidelite.map((transaction) => {
                          const row = toHistoryRowView(transaction);
                          return (
                            <div
                              key={row.id}
                              className="flex items-start justify-between border border-gray-200 rounded-lg p-4"
                            >
                              <div className="flex items-start">
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                                    row.credit ? 'bg-green-100' : 'bg-red-100'
                                  }`}
                                >
                                  <TrendingUp
                                    className={`h-4 w-4 ${
                                      row.credit ? 'text-green-600' : 'text-red-600 rotate-180'
                                    }`}
                                  />
                                </div>
                                <div>
                                  <p
                                    className={`font-semibold ${
                                      row.credit ? 'text-green-700' : 'text-red-700'
                                    }`}
                                  >
                                    {row.pointsLabel}
                                  </p>
                                  {row.title && (
                                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                                      {row.title}
                                    </p>
                                  )}
                                  {row.subtitle && (
                                    <p className="text-sm text-gray-500 mt-0.5">{row.subtitle}</p>
                                  )}
                                  {(row.secondaryNumero || row.secondaryId) && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      Réf.{' '}
                                      {row.secondaryNumero ||
                                        (row.secondaryId
                                          ? `#${String(row.secondaryId).slice(-8)}`
                                          : '')}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Gift className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          Aucune transaction
                        </h3>
                        <p className="text-gray-500">
                          Aucune transaction de points pour le moment.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Super admin sans commerce ciblé — Clickorico séparé, pas de catalogue multi-boutiques */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <Award className="h-5 w-5 mr-2" />
                    Points Clickorico (plateforme)
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Aucun commerce ciblé : la fidélité boutique F2 n’est pas affichée ici.
                    Ouvrez la fiche depuis un commerçant pour voir le catalogue boutique.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Solde Clickorico</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {client.pointsClickorico ?? client.pointsFidelite ?? 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-500">Niveau plateforme</p>
                      <p className="text-xl font-bold text-gray-900">
                        {client.niveauFidelite || 'Non renseigné'}
                      </p>
                    </div>
                  </div>

                  {currentUser?.role === 'SUPER_ADMIN' && (
                    <form
                      onSubmit={handleAdjustPoints}
                      className="mt-6 pt-6 border-t border-gray-100 space-y-3"
                    >
                      <h4 className="text-sm font-semibold text-gray-900">
                        Ajustement manuel (super admin)
                      </h4>
                      <p className="text-xs text-gray-500">
                        Crédit ou débit du solde Clickorico en cas de bug d’attribution.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Points (+ ou −)
                          </label>
                          <input
                            type="number"
                            value={pointsAdjustForm.points}
                            onChange={(e) =>
                              setPointsAdjustForm((prev) => ({ ...prev, points: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="ex. 215"
                            required
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Raison
                          </label>
                          <input
                            type="text"
                            value={pointsAdjustForm.raison}
                            onChange={(e) =>
                              setPointsAdjustForm((prev) => ({ ...prev, raison: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="ex. Rattrapage commande livrée sans points"
                            required
                            minLength={3}
                          />
                        </div>
                      </div>
                      {pointsAdjustError && (
                        <p className="text-sm text-red-600">{pointsAdjustError}</p>
                      )}
                      {pointsAdjustMessage && (
                        <p className="text-sm text-green-700">{pointsAdjustMessage}</p>
                      )}
                      <button
                        type="submit"
                        disabled={pointsAdjustLoading}
                        className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 disabled:opacity-60"
                      >
                        {pointsAdjustLoading ? 'Enregistrement…' : 'Appliquer l’ajustement'}
                      </button>
                    </form>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Modal détail commande */}
        {orderModal.open && (
          <Modal
            isOpen={orderModal.open}
            title={
              orderModal.commande
                ? `Commande ${
                    orderModal.commande.numero ||
                    (orderModal.commande.id ? `#${orderModal.commande.id.slice(-8)}` : '')
                  }`
                : 'Commande'
            }
            onClose={closeOrderModal}
          >
            {orderModal.loading ? (
              <div className="py-10 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-sm text-gray-600">Chargement des détails…</p>
              </div>
            ) : orderModal.error ? (
              <div className="space-y-4">
                <p className="text-red-600 text-sm">{orderModal.error}</p>
                <div className="flex justify-end gap-3">
                  <button
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                    onClick={closeOrderModal}
                  >
                    Fermer
                  </button>
                  <button
                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    onClick={() => handleViewOrderDetail(orderModal.commandeId)}
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            ) : orderModal.commande ? (
                <div className="space-y-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        Passée le{' '}
                        {new Date(orderModal.commande.dateCommande).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <p className="text-sm text-gray-500">
                        Mode : {orderModal.commande.modeRetrait === 'clickandcollect' ? 'Click & Collect' : 'Livraison'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(orderModal.commande.statut)}`}>
                        {getStatusIcon(orderModal.commande.statut)}
                        <span className="ml-1">{orderModal.commande.statut}</span>
                      </span>
                      <button
                        onClick={() => navigate(`/commandes?commandeId=${orderModal.commande.id}`)}
                        className="text-sm text-indigo-600 hover:text-indigo-900"
                      >
                        Ouvrir dans Commandes
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Articles</h4>
                    {orderModal.commande.produits?.length ? (
                      <div className="space-y-3">
                        {orderModal.commande.produits.map((item) => (
                          <div key={item.id} className="flex justify-between items-start border rounded-lg p-3">
                            <div>
                              <p className="font-medium text-gray-900">{item.produit?.nom || item.nom}</p>
                              <p className="text-sm text-gray-500">
                                {item.quantite} × {formatAmount(item.prixUnitaire)}€
                                {item.commentaire && (
                                  <span className="ml-2 italic text-gray-400">({item.commentaire})</span>
                                )}
                              </p>
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatAmount(item.prixTotal || item.prixUnitaire * item.quantite)}€
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Aucun article listé.</p>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sous-total</span>
                      <span className="font-medium">
                        {formatAmount(orderModal.commande.sousTotal || orderModal.commande.total)}€
                      </span>
                    </div>
                    {orderModal.commande.fraisLivraison !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Frais de livraison</span>
                        <span className="font-medium">{formatAmount(orderModal.commande.fraisLivraison)}€</span>
                      </div>
                    )}
                    {(() => {
                      const { parts, total: reductionTotal } =
                        buildReductionDisplayLines(orderModal.commande);
                      return (
                        <>
                          {parts.map((part) => (
                            <div
                              key={part.key}
                              className="flex justify-between text-emerald-600"
                            >
                              <span>{part.label}</span>
                              <span>-{formatAmount(part.amount)}€</span>
                            </div>
                          ))}
                          {parts.length > 1 && (
                            <div className="flex justify-between text-emerald-700 font-medium">
                              <span>Total réductions</span>
                              <span>-{formatAmount(reductionTotal)}€</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div className="flex justify-between text-base font-bold pt-2 border-t border-gray-200">
                      <span>Total payé</span>
                      <span>{formatAmount(orderModal.commande.total)}€</span>
                    </div>
                  </div>

                  {orderModal.commande.adresseLivraison && (
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Adresse</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-line">
                        {orderModal.commande.adresseLivraison}
                      </p>
                    </div>
                  )}

                  {orderModal.commande.commentaireClient && (
                    <div className="border rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Commentaire client</h4>
                      <p className="text-sm text-gray-600">{orderModal.commande.commentaireClient}</p>
                    </div>
                  )}
                </div>
            ) : (
              <p className="text-sm text-gray-600">Aucune donnée pour cette commande.</p>
            )}
          </Modal>
        )}
      </div>
      <Modal
        isOpen={emailModalOpen}
        onClose={() => {
          if (emailSending) return;
          setEmailModalOpen(false);
          setEmailError('');
        }}
        title="Envoyer un email"
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleSendClientEmail} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">À</label>
            <input
              type="text"
              readOnly
              value={client?.email || ''}
              className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
            <input
              type="text"
              maxLength={150}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              rows={6}
              maxLength={4000}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>
          {emailError && (
            <p className="text-sm text-red-700">{emailError}</p>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700"
              disabled={emailSending}
              onClick={() => {
                setEmailModalOpen(false);
                setEmailError('');
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50"
              disabled={emailSending || !emailSubject.trim() || !emailMessage.trim()}
            >
              {emailSending ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        </form>
      </Modal>
      {toasts.map((toastItem) => (
        <Toast key={toastItem.id} toast={toastItem} onClose={() => removeToast(toastItem.id)} />
      ))}
    </div>
  );
};

export default ClientProfileComplete;





